import React, { useState, useEffect } from 'react';
import { X, Check, AlertCircle } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';

/**
 * Modal for batch removing responsible persons from selected entities.
 * Shows currently assigned persons; clicking X marks them for removal.
 */
const BatchRemoveResponsibleModal = ({ entityType, selectedIds, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [assigned, setAssigned] = useState({});
  const [loading, setLoading] = useState(true);
  const [toRemove, setToRemove] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(0);
  const [errors, setErrors] = useState([]);

  // Fetch current assigned persons
  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    selectedIds.forEach(id => params.append('ids[]', id));

    fetch(`/api/${entityType}s/batch-responsible-persons?${params.toString()}`)
      .then(r => r.json())
      .then(data => {
        if (data.status === 'success') {
          setAssigned(data.data || {});
        } else {
          setAssigned({});
        }
      })
      .catch(() => setAssigned({}))
      .finally(() => setLoading(false));
  }, [entityType, selectedIds]);

  const toggleRemove = (entityId, rpId) => {
    setToRemove(prev => {
      const key = `${entityId}-${rpId}`;
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
      } else {
        next[key] = { entityId, rpId };
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    const items = Object.values(toRemove);
    if (items.length === 0) return;

    setSubmitting(true);
    setErrors([]);
    setDone(0);

    const total = items.length;
    const errs = [];

    for (let i = 0; i < total; i++) {
      const { entityId, rpId } = items[i];
      try {
        const res = await fetch(`/api/${entityType}s/${entityId}/responsible-persons/remove`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: rpId }),
        });
        const data = await res.json();
        if (data.status !== 'success') {
          errs.push(`ID ${entityId}: ${data.message || t('common.request_failed')}`);
        }
      } catch (e) {
        errs.push(`ID ${entityId}: ${e.message}`);
      }
      setDone(i + 1);
    }

    setErrors(errs);
    setSubmitting(false);

    if (errs.length === 0) {
      onSuccess();
    }
  };

  const totalAssigned = Object.values(assigned).reduce((sum, list) => sum + list.length, 0);
  const removeCount = Object.keys(toRemove).length;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[99999]" onClick={onClose}>
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
            {t('common.batch_remove_responsible')}
          </h2>
          <button onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4 flex-1 overflow-y-auto">
          <div className="text-sm text-slate-500 dark:text-slate-400">
            <span dangerouslySetInnerHTML={{ __html: t('common.selected_records_count_remove', { count: selectedIds.length }) }} />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-6 h-6 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : totalAssigned === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <AlertCircle size={36} className="mb-3" />
              <p className="text-sm">{t('common.no_responsible_persons')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {selectedIds.map(entityId => {
                const persons = assigned[entityId] || [];
                if (persons.length === 0) return null;

                return (
                  <div key={entityId} className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
                    <div className="bg-slate-50 dark:bg-slate-900/50 px-4 py-2 border-b border-slate-200 dark:border-slate-700">
                      <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                        {t('common.entity_id_label')}: #{entityId}
                      </span>
                    </div>
                    <div className="divide-y divide-slate-100 dark:divide-slate-700">
                      {persons.map(p => {
                        const rpKey = `${entityId}-${p.id}`;
                        const marked = !!toRemove[rpKey];
                        const displayName = [(p.first_name || ''), (p.last_name || '')].filter(Boolean).join('');

                        return (
                          <div key={p.id}
                            className={`flex items-center justify-between px-4 py-3 transition-colors ${
                              marked
                                ? 'bg-red-50 dark:bg-red-900/20 line-through text-red-500'
                                : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                            }`}>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-slate-400 w-8">{p.position_name || `#${p.position_id}`}</span>
                              <span className="text-sm text-slate-700 dark:text-slate-300">
                                {displayName || `#${p.employee_id}`}
                              </span>
                            </div>
                            {!submitting && (
                              <button
                                onClick={() => toggleRemove(entityId, p.id)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                title={marked ? t('common.cancel_remove') : t('common.remove')}
                              >
                                <X size={16} />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Progress */}
          {submitting && (
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-slate-500">
                <span>{t('common.removing_in_progress')}</span>
                <span>{done} / {removeCount}</span>
              </div>
              <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                <div className="bg-red-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(done / removeCount) * 100}%` }} />
              </div>
            </div>
          )}

          {/* Errors */}
          {errors.length > 0 && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 max-h-32 overflow-y-auto">
              <p className="text-sm font-medium text-red-600 dark:text-red-400 mb-1">{t('common.remove_partial_failure')}</p>
              {errors.map((err, i) => (
                <p key={i} className="text-xs text-red-500 dark:text-red-300">{err}</p>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          <button onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors">
            {t('common.cancel')}
          </button>
          <button onClick={handleSubmit}
            disabled={removeCount === 0 || submitting}
            className="flex items-center gap-2 bg-red-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-all">
            <Check size={16} />
            {submitting ? t('common.removing_in_progress') : t('common.remove_with_count', { count: removeCount })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BatchRemoveResponsibleModal;
